/**
 * Native SVG Pan/Zoom Utility
 *
 * Provides crisp pan/zoom functionality for SVG elements using viewBox manipulation.
 * No external dependencies - pure TypeScript implementation.
 *
 * Usage:
 *   const cleanup = initializeSvgPanZoom(svgElement, {
 *     scrollZoomEnabled: true,
 *     onZoomChange: (scale) => console.log('Zoom:', scale)
 *   });
 *
 *   // Later, to cleanup:
 *   cleanup();
 */

export interface SvgPanZoomState {
  scale: number;
  panX: number;
  panY: number;
  originalViewBox: { x: number; y: number; width: number; height: number };
  isDragging: boolean;
  lastMouseX: number;
  lastMouseY: number;
}

export interface SvgPanZoomOptions {
  /** Whether scroll wheel zooming is enabled (default: false) */
  scrollZoomEnabled?: boolean;
  /** Minimum zoom scale (default: 0.1) */
  minScale?: number;
  /** Maximum zoom scale (default: 50) */
  maxScale?: number;
  /** Zoom factor per scroll/button click (default: 1.25 for in, 0.8 for out) */
  zoomInFactor?: number;
  zoomOutFactor?: number;
  /** Callback when zoom changes */
  onZoomChange?: (scale: number) => void;
}

export interface SvgPanZoomController {
  /** Cleanup function to remove event listeners */
  cleanup: () => void;
  /** Get current state */
  getState: () => SvgPanZoomState;
  /** Zoom in */
  zoomIn: () => void;
  /** Zoom out */
  zoomOut: () => void;
  /** Reset to original view */
  reset: () => void;
  /** Fit diagram to container */
  fit: () => void;
  /** Update scroll zoom enabled state */
  setScrollZoomEnabled: (enabled: boolean) => void;
}

/**
 * Initialize pan/zoom controls on an SVG element
 * Returns a controller object with methods to control zoom and a cleanup function
 */
export function initializeSvgPanZoom(
  svgElement: SVGSVGElement,
  options: SvgPanZoomOptions = {}
): SvgPanZoomController {
  const {
    minScale = 0.1,
    maxScale = 50,
    zoomInFactor = 1.25,
    zoomOutFactor = 0.8,
    onZoomChange,
  } = options;

  let scrollZoomEnabled = options.scrollZoomEnabled ?? false;

  // Get the original viewBox or compute from bounding box
  const viewBoxAttr = svgElement.getAttribute('viewBox');
  let originalViewBox: SvgPanZoomState['originalViewBox'];

  if (viewBoxAttr) {
    const [x, y, width, height] = viewBoxAttr.split(' ').map(Number);
    originalViewBox = { x: x!, y: y!, width: width!, height: height! };
  } else {
    const bbox = svgElement.getBBox();
    originalViewBox = { x: bbox.x, y: bbox.y, width: bbox.width, height: bbox.height };
    svgElement.setAttribute('viewBox', `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`);
  }

  const state: SvgPanZoomState = {
    scale: 1,
    panX: 0,
    panY: 0,
    originalViewBox,
    isDragging: false,
    lastMouseX: 0,
    lastMouseY: 0,
  };

  svgElement.style.width = '100%';
  svgElement.style.height = '100%';
  svgElement.style.cursor = 'grab';
  svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');

  const updateViewBox = () => {
    const { originalViewBox: vb, scale, panX, panY } = state;
    const newWidth = vb.width / scale;
    const newHeight = vb.height / scale;
    // panX/panY are already in viewBox units, so apply directly
    const newX = vb.x + (vb.width - newWidth) / 2 - panX;
    const newY = vb.y + (vb.height - newHeight) / 2 - panY;
    svgElement.setAttribute('viewBox', `${newX} ${newY} ${newWidth} ${newHeight}`);
    onZoomChange?.(state.scale);
  };

  // Mouse wheel zoom (only when scroll zoom is enabled)
  const handleWheel = (e: WheelEvent) => {
    if (!scrollZoomEnabled) return; // Allow normal scrolling when disabled
    e.preventDefault();
    const zoomFactor = e.deltaY > 0 ? zoomOutFactor : zoomInFactor;
    state.scale = Math.min(Math.max(state.scale * zoomFactor, minScale), maxScale);
    updateViewBox();
  };

  // Pan on drag
  const handleMouseDown = (e: MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault(); // Prevent text selection and default drag behavior
    state.isDragging = true;
    state.lastMouseX = e.clientX;
    state.lastMouseY = e.clientY;
    svgElement.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!state.isDragging) return;
    e.preventDefault(); // Prevent any default behavior during drag
    const dx = e.clientX - state.lastMouseX;
    const dy = e.clientY - state.lastMouseY;

    // Convert screen pixels to viewBox units
    // Account for preserveAspectRatio="xMidYMid meet" which maintains aspect ratio
    const containerRect = svgElement.getBoundingClientRect();
    const { originalViewBox: vb, scale } = state;
    const currentViewBoxWidth = vb.width / scale;
    const currentViewBoxHeight = vb.height / scale;

    // Calculate the actual rendered size (accounting for aspect ratio preservation)
    const viewBoxAspect = currentViewBoxWidth / currentViewBoxHeight;
    const containerAspect = containerRect.width / containerRect.height;

    let renderedWidth: number;
    let renderedHeight: number;

    if (viewBoxAspect > containerAspect) {
      // ViewBox is wider - width fills container, height is letterboxed
      renderedWidth = containerRect.width;
      renderedHeight = containerRect.width / viewBoxAspect;
    } else {
      // ViewBox is taller - height fills container, width is letterboxed
      renderedHeight = containerRect.height;
      renderedWidth = containerRect.height * viewBoxAspect;
    }

    // Scale mouse movement to viewBox coordinates using actual rendered size
    const viewBoxDx = (dx / renderedWidth) * currentViewBoxWidth;
    const viewBoxDy = (dy / renderedHeight) * currentViewBoxHeight;

    state.panX += viewBoxDx;
    state.panY += viewBoxDy;
    state.lastMouseX = e.clientX;
    state.lastMouseY = e.clientY;
    updateViewBox();
  };

  const handleMouseUp = () => {
    state.isDragging = false;
    svgElement.style.cursor = 'grab';
  };

  // Double-click to reset
  const handleDblClick = () => {
    state.scale = 1;
    state.panX = 0;
    state.panY = 0;
    updateViewBox();
  };

  svgElement.addEventListener('wheel', handleWheel, { passive: false });
  svgElement.addEventListener('mousedown', handleMouseDown);
  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
  svgElement.addEventListener('dblclick', handleDblClick);

  // Cleanup function
  const cleanup = () => {
    svgElement.removeEventListener('wheel', handleWheel);
    svgElement.removeEventListener('mousedown', handleMouseDown);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    svgElement.removeEventListener('dblclick', handleDblClick);
  };

  // Controller methods
  const zoomIn = () => {
    state.scale = Math.min(state.scale * zoomInFactor, maxScale);
    updateViewBox();
  };

  const zoomOut = () => {
    state.scale = Math.max(state.scale * zoomOutFactor, minScale);
    updateViewBox();
  };

  const reset = () => {
    state.scale = 1;
    state.panX = 0;
    state.panY = 0;
    updateViewBox();
  };

  const fit = () => {
    // Fit the diagram content to fill the container
    const bbox = svgElement.getBBox();
    const containerRect = svgElement.getBoundingClientRect();

    // Check if we have valid dimensions to calculate fit
    const hasValidBBox = bbox.width > 0 && bbox.height > 0;
    // Only use fit calculation if the SVG is in a properly sized container
    // SVGs appended directly to body (like in tests) don't have meaningful container dimensions
    const isInSizedContainer =
      containerRect.width > 1 &&
      containerRect.height > 1 &&
      svgElement.parentElement !== document.body;

    if (hasValidBBox && isInSizedContainer) {
      const scaleX = containerRect.width / bbox.width;
      const scaleY = containerRect.height / bbox.height;
      // Use the smaller scale to ensure full content is visible
      const fitScale = Math.min(scaleX, scaleY) * 0.95; // 95% to add some padding

      state.scale = Math.min(Math.max(fitScale, minScale), maxScale);
      state.panX = 0;
      state.panY = 0;
      updateViewBox();
    } else {
      // Fallback to reset if we can't calculate fit (e.g., in test environments)
      reset();
    }
  };

  const setScrollZoomEnabled = (enabled: boolean) => {
    scrollZoomEnabled = enabled;
  };

  return {
    cleanup,
    getState: () => ({ ...state }),
    zoomIn,
    zoomOut,
    reset,
    fit,
    setScrollZoomEnabled,
  };
}

/**
 * Manager for multiple SVG pan/zoom instances
 * Useful when rendering multiple diagrams that need coordinated controls
 */
export class SvgPanZoomManager {
  private controllers: Map<string, SvgPanZoomController> = new Map();
  private scrollZoomEnabled = false;

  /**
   * Add an SVG element to be managed
   */
  add(id: string, svgElement: SVGSVGElement, options: SvgPanZoomOptions = {}) {
    // Remove existing if present
    this.remove(id);

    const controller = initializeSvgPanZoom(svgElement, {
      ...options,
      scrollZoomEnabled: this.scrollZoomEnabled,
    });
    this.controllers.set(id, controller);
  }

  /**
   * Remove and cleanup an SVG element
   */
  remove(id: string) {
    const controller = this.controllers.get(id);
    if (controller) {
      controller.cleanup();
      this.controllers.delete(id);
    }
  }

  /**
   * Cleanup all managed SVG elements
   */
  cleanup() {
    for (const controller of this.controllers.values()) {
      controller.cleanup();
    }
    this.controllers.clear();
    this.scrollZoomEnabled = false;
  }

  /**
   * Zoom in all diagrams
   */
  zoomInAll() {
    for (const controller of this.controllers.values()) {
      controller.zoomIn();
    }
  }

  /**
   * Zoom out all diagrams
   */
  zoomOutAll() {
    for (const controller of this.controllers.values()) {
      controller.zoomOut();
    }
  }

  /**
   * Reset all diagrams to original view
   */
  resetAll() {
    for (const controller of this.controllers.values()) {
      controller.reset();
    }
  }

  /**
   * Fit all diagrams to their containers
   */
  fitAll() {
    for (const controller of this.controllers.values()) {
      controller.fit();
    }
  }

  /**
   * Toggle scroll zoom for all diagrams
   */
  setScrollZoomEnabled(enabled: boolean) {
    this.scrollZoomEnabled = enabled;
    for (const controller of this.controllers.values()) {
      controller.setScrollZoomEnabled(enabled);
    }
  }

  /**
   * Get scroll zoom enabled state
   */
  isScrollZoomEnabled(): boolean {
    return this.scrollZoomEnabled;
  }

  /**
   * Get number of managed diagrams
   */
  get size(): number {
    return this.controllers.size;
  }
}
